// Ordering helpers shared by the admin "Created Games" list and the game
// importer's review step. Both surfaces drive a game's `displayOrder`, which
// the player-facing week view orders by (see DataContext).

// Immutable move of one item within a list, as produced by a drag-and-drop
// result. Out-of-range or same-position moves return the original list.
export const reorder = (list, fromIndex, toIndex) => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 || fromIndex >= list.length ||
    toIndex < 0 || toIndex >= list.length
  ) {
    return list;
  }
  const items = [...list];
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);
  return items;
};

// Rankings arrive as numbers from the odds-events function and as free-text
// strings once the admin can edit them in the review table ('', '#', '14').
export const hasRanking = (value) => Number(value) > 0;

// A Top 25 matchup means BOTH teams carry a ranking — the same definition the
// importer's browse-step filter uses.
export const isTop25Matchup = (game) =>
  hasRanking(game.homeRanking) && hasRanking(game.awayRanking);

// The pool's home teams (team doc ids). Their games default to Pick the Score
// on import; the admin can still change any game on the review step.
export const PICK_SCORE_TEAM_IDS = ['byu', 'utah'];

// Import default: a BYU/Utah game is Pick the Score, and Score + Winner when
// it's also a Top 25 matchup. Everything else is Pick the Winner.
export const defaultScoringType = (game) => {
  const featured = [game.homeTeamId, game.awayTeamId].some((id) => PICK_SCORE_TEAM_IDS.includes(id));
  if (!featured) return 'winner';
  return isTop25Matchup(game) ? 'scoreWinner' : 'score';
};

export const isPickScoreGame = (game) =>
  game.scoringType === 'score' ||
  game.scoringType === 'scoreWinner' ||
  game.isPickScore === true;

// Standard review order: pick-the-score games first, then Top 25 matchups,
// then everything else — each tier by kickoff time.
const tierOf = (game) => {
  if (isPickScoreGame(game)) return 0;
  if (isTop25Matchup(game)) return 1;
  return 2;
};

const kickoffMs = (game) => {
  const t = game.startTime;
  const ms = t?.toMillis ? t.toMillis() : new Date(t).getTime();
  return Number.isNaN(ms) ? Infinity : ms;
};

export const compareReviewOrder = (a, b) =>
  (tierOf(a) - tierOf(b)) || (kickoffMs(a) - kickoffMs(b));

// Array.prototype.sort is stable, so games tied on tier and kickoff keep
// their incoming relative order.
export const sortReviewGames = (games) => [...games].sort(compareReviewOrder);
